import {
  deriveEmotionalRead,
  type EmotionDetectionLike,
} from '../emotionalRead.helpers';

interface StorySummaryLike {
  summary?: string | null;
  key_topics?: (string | null)[] | null;
}

interface Props {
  emotion: EmotionDetectionLike | null | undefined;
  storySummary: StorySummaryLike | null | undefined;
}

/**
 * Verdict-first lead band shown between the score hero and the numbered
 * sections. States the analyzer's emotional read ("Highly emotional ·
 * fear-driven, anger underneath · 84%"), the one-line story summary, and topic
 * chips — all from data the analyzer already produces. Each element renders
 * only when its source is present; the whole band hides when none are.
 */
export default function EmotionalRead({ emotion, storySummary }: Props) {
  const read = deriveEmotionalRead(emotion);
  const summary = storySummary?.summary?.trim() || '';
  const topics = (storySummary?.key_topics ?? []).filter(
    (t): t is string => typeof t === 'string' && t.trim().length > 0,
  );

  if (!read && !summary && topics.length === 0) return null;

  return (
    <section
      className={`POST__ANALYSIS__read POST__ANALYSIS__read--${read?.stateTone ?? 'calm'}`}
      aria-label="Emotional read"
    >
      <div className="POST__ANALYSIS__read__eyebrow">Emotional read</div>

      {read && (
        <div className="POST__ANALYSIS__read__head">
          <h3 className="POST__ANALYSIS__read__state">{read.stateLabel}</h3>
          {read.intensityPct !== null && (
            <div className="POST__ANALYSIS__read__intensity">
              <span>intensity</span>
              <span className="POST__ANALYSIS__read__meter">
                <i style={{ width: `${read.intensityPct}%` }} />
              </span>
              <span>{read.intensityPct}%</span>
            </div>
          )}
        </div>
      )}

      {read?.phrasing && (
        <p className="POST__ANALYSIS__read__phrase">{read.phrasing}</p>
      )}

      {summary && <p className="POST__ANALYSIS__read__summary">{summary}</p>}

      {topics.length > 0 && (
        <div className="POST__ANALYSIS__read__about">
          <span className="POST__ANALYSIS__read__lbl">About</span>
          {topics.map((t, i) => (
            <span key={`${t}-${i}`} className="POST__ANALYSIS__read__chip">
              {t}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
